import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, Users, GraduationCap, BarChart3, Award, Shield, 
  Play, Eye, Settings, FileText, CheckCircle2, ArrowRight, ArrowLeft,
  Lock, Zap, BookOpen, Target, Clock, TrendingUp, Download, Upload,
  UserCheck, ClipboardCheck, Medal, QrCode, Monitor, Laptop, Mail,
  UserPlus, FileSpreadsheet, LogIn, Home, ChevronRight, AlertCircle,
  PlusCircle, Search, Filter, MoreVertical, Edit, Trash2, RefreshCw, Crown
} from 'lucide-react';

const PRACTIQ_LOGO = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";
const DEMO_INSTITUTE_LOGO = "https://upload.wikimedia.org/wikipedia/en/thumb/4/41/Flag_of_India.svg/1200px-Flag_of_India.svg.png";

const DemoInstitutePage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('landing'); // landing, admin-login, admin-dashboard, student-login, student-dashboard
  const [examMode, setExamMode] = useState(false);

  // Mock data
  const mockStudents = [
    { id: 1, name: 'Rahul Sharma', email: 'rahul@demo.edu', batch: 'Batch A', gstr1: 92, gstr3b: 88, status: 'Active', lastActive: '2 min ago' },
    { id: 2, name: 'Priya Patel', email: 'priya@demo.edu', batch: 'Batch A', gstr1: 95, gstr3b: 91, status: 'Active', lastActive: '5 min ago' },
    { id: 3, name: 'Amit Kumar', email: 'amit@demo.edu', batch: 'Batch B', gstr1: 78, gstr3b: 82, status: 'Active', lastActive: '1 hour ago' },
    { id: 4, name: 'Sneha Gupta', email: 'sneha@demo.edu', batch: 'Batch A', gstr1: 88, gstr3b: 0, status: 'In Progress', lastActive: '15 min ago' },
    { id: 5, name: 'Vikram Singh', email: 'vikram@demo.edu', batch: 'Batch B', gstr1: 0, gstr3b: 0, status: 'Not Started', lastActive: '2 days ago' },
  ];

  const mockBatches = [
    { name: 'Batch A', students: 25, avgAccuracy: 89, created: 'Jan 15, 2025' },
    { name: 'Batch B', students: 30, avgAccuracy: 82, created: 'Jan 20, 2025' },
  ];

  // Landing Page - Institute Portal Entry
  const LandingView = () => (
    <div className="min-h-screen bg-[#0A0F1C]">
      {/* Co-branded Header */}
      <div className="w-full bg-[#111827] border-b border-white/10 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-10 h-10 object-contain" />
            <span className="text-2xl text-gray-600 font-light">×</span>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-semibold text-white">Demo University</span>
            </div>
          </div>
          <Badge className="bg-blue-500/20 text-blue-400 border-0">
            Partner Portal
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4 py-12">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Welcome to Demo University
            </h1>
            <p className="text-lg text-gray-400">
              GST & Tax Filing Simulation Platform powered by PractiQ
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Faculty/Admin Card */}
            <Card 
              className="bg-[#111827] border-white/10 hover:border-amber-500/50 transition-all cursor-pointer group"
              onClick={() => setView('admin-login')}
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2">Faculty Portal</h2>
                <p className="text-gray-400 mb-6">
                  Manage students, view analytics, enable exam mode, and generate certificates
                </p>
                <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white">
                  <LogIn className="w-4 h-4 mr-2" />
                  Faculty Login
                </Button>
              </CardContent>
            </Card>

            {/* Student Card */}
            <Card 
              className="bg-[#111827] border-white/10 hover:border-teal-500/50 transition-all cursor-pointer group"
              onClick={() => setView('student-login')}
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <GraduationCap className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2">Student Portal</h2>
                <p className="text-gray-400 mb-6">
                  Practice GST returns, track progress, and earn verified certificates
                </p>
                <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white">
                  <LogIn className="w-4 h-4 mr-2" />
                  Student Login
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <Button variant="link" className="text-gray-500 hover:text-gray-300" onClick={() => navigate('/for-business')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to PractiQ Pro
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Admin Login
  const AdminLoginView = () => (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center px-4">
      <Card className="bg-[#111827] border-white/10 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Faculty Login</CardTitle>
          <CardDescription className="text-gray-400">
            Demo University Admin Portal
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input 
                type="email" 
                placeholder="admin@demo.edu" 
                className="bg-white/5 border-white/10 text-white"
                defaultValue="admin@demo.edu"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Password</Label>
              <Input 
                type="password" 
                placeholder="••••••••" 
                className="bg-white/5 border-white/10 text-white"
                defaultValue="demo123"
              />
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
              onClick={() => setView('admin-dashboard')}
            >
              Sign In
            </Button>
          </div>
          <div className="mt-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-amber-400 text-center">
              <strong>Demo Credentials:</strong> admin@demo.edu / demo123
            </p>
          </div>
          <Button variant="link" className="w-full mt-4 text-gray-500" onClick={() => setView('landing')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Student Login
  const StudentLoginView = () => (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center px-4">
      <Card className="bg-[#111827] border-white/10 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Student Login</CardTitle>
          <CardDescription className="text-gray-400">
            Demo University - GST Simulation
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input 
                type="email" 
                placeholder="your.email@demo.edu" 
                className="bg-white/5 border-white/10 text-white"
                defaultValue="rahul@demo.edu"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Password</Label>
              <Input 
                type="password" 
                placeholder="••••••••" 
                className="bg-white/5 border-white/10 text-white"
                defaultValue="student123"
              />
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400"
              onClick={() => setView('student-dashboard')}
            >
              Sign In
            </Button>
          </div>
          <div className="mt-6 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20">
            <p className="text-xs text-teal-400 text-center">
              <strong>Demo Credentials:</strong> rahul@demo.edu / student123
            </p>
          </div>
          <Button variant="link" className="w-full mt-4 text-gray-500" onClick={() => setView('landing')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Admin Dashboard
  const AdminDashboardView = () => (
    <div className="min-h-screen bg-[#0A0F1C]">
      {/* Admin Header */}
      <div className="w-full bg-[#111827] border-b border-white/10 py-3 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-8 h-8" />
            <span className="text-lg font-semibold text-white">Demo University - Faculty Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Badge className="bg-amber-500/20 text-amber-400 border-0">
              <Shield className="w-3 h-3 mr-1" /> Faculty
            </Badge>
            <Button variant="ghost" size="sm" className="text-gray-400" onClick={() => setView('landing')}>
              <LogIn className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stats */}
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Students', value: '55', icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Active Now', value: '12', icon: Monitor, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Avg. Accuracy', value: '85.5%', icon: Target, color: 'text-amber-400', bg: 'bg-amber-500/10' },
            { label: 'Scorecards', value: '38', icon: Award, color: 'text-purple-400', bg: 'bg-purple-500/10' },
            { label: 'License Used', value: '55/200', icon: Shield, color: 'text-teal-400', bg: 'bg-teal-500/10' }
          ].map((stat, i) => (
            <div key={i} className={`p-4 rounded-xl ${stat.bg} border border-white/10`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">{stat.label}</span>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Exam Mode Toggle */}
            <Card className="bg-[#111827] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${examMode ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
                      {examMode ? <Lock className="w-5 h-5 text-red-400" /> : <BookOpen className="w-5 h-5 text-emerald-400" />}
                    </div>
                    <div>
                      <h3 className="font-medium text-white">Assessment Mode</h3>
                      <p className="text-xs text-gray-400">
                        {examMode ? 'Assessment Active - Hints disabled, tracking accuracy' : 'Practice Mode - Students can use hints'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className={examMode ? 'bg-red-500 hover:bg-red-400' : 'bg-emerald-500 hover:bg-emerald-400'}
                    onClick={() => setExamMode(!examMode)}
                  >
                    {examMode ? 'Disable Assessment' : 'Enable Assessment'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Students Table */}
            <Card className="bg-[#111827] border-white/10">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white">Student Progress & Employability</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="border-white/20 text-gray-300">
                      <Upload className="w-4 h-4 mr-1" /> Upload Batch
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/20 text-gray-300">
                      <Download className="w-4 h-4 mr-1" /> Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr className="text-left text-xs text-gray-400">
                        <th className="px-4 py-3">Student</th>
                        <th className="px-4 py-3">Batch</th>
                        <th className="px-4 py-3">GSTR-1</th>
                        <th className="px-4 py-3">GSTR-3B</th>
                        <th className="px-4 py-3">Employability</th>
                        <th className="px-4 py-3">Last Active</th>
                        <th className="px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockStudents.map((student) => (
                        <tr key={student.id} className="border-t border-white/5 text-sm">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-white font-medium">{student.name}</p>
                              <p className="text-xs text-gray-500">{student.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-400">{student.batch}</td>
                          <td className="px-4 py-3">
                            {student.gstr1 > 0 ? (
                              <span className={student.gstr1 >= 90 ? 'text-emerald-400' : student.gstr1 >= 80 ? 'text-amber-400' : 'text-red-400'}>
                                {student.gstr1}%
                              </span>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {student.gstr3b > 0 ? (
                              <span className={student.gstr3b >= 90 ? 'text-emerald-400' : student.gstr3b >= 80 ? 'text-amber-400' : 'text-red-400'}>
                                {student.gstr3b}%
                              </span>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              student.gstr1 >= 85 && student.gstr3b >= 85 ? 'bg-emerald-500/20 text-emerald-400 border-0' :
                              student.status === 'In Progress' ? 'bg-amber-500/20 text-amber-400 border-0' :
                              'bg-gray-500/20 text-gray-400 border-0'
                            }>
                              {student.gstr1 >= 85 && student.gstr3b >= 85 ? 'Job Ready' : student.status === 'In Progress' ? 'In Progress' : 'Not Started'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{student.lastActive}</td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Batches */}
            <Card className="bg-[#111827] border-white/10">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base text-white">Batches</CardTitle>
                  <Button size="sm" variant="ghost" className="text-teal-400">
                    <PlusCircle className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mockBatches.map((batch, i) => (
                    <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{batch.name}</span>
                        <Badge className="bg-teal-500/20 text-teal-400 border-0 text-xs">
                          {batch.students} students
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Avg: {batch.avgAccuracy}%</span>
                        <span className="text-gray-500">{batch.created}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-[#111827] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start border-white/10 text-gray-300 hover:bg-white/5">
                  <Upload className="w-4 h-4 mr-2 text-blue-400" /> Upload Student List
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-gray-300 hover:bg-white/5">
                  <Award className="w-4 h-4 mr-2 text-amber-400" /> Generate Scorecards
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-gray-300 hover:bg-white/5">
                  <BarChart3 className="w-4 h-4 mr-2 text-emerald-400" /> View Analytics
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-gray-300 hover:bg-white/5">
                  <Settings className="w-4 h-4 mr-2 text-purple-400" /> Portal Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  // Student Dashboard
  const StudentDashboardView = () => (
    <div className="min-h-screen bg-[#0A0F1C]">
      {/* Student Header */}
      <div className="w-full bg-[#111827] border-b border-white/10 py-3 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-8 h-8" />
            <span className="text-2xl text-gray-600">×</span>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-white">Demo University</span>
          </div>
          <div className="flex items-center gap-4">
            {examMode && (
              <Badge className="bg-red-500/20 text-red-400 border-0 animate-pulse">
                <Lock className="w-3 h-3 mr-1" /> Assessment Mode Active
              </Badge>
            )}
            <div className="text-right">
              <p className="text-sm text-white">Rahul Sharma</p>
              <p className="text-xs text-gray-500">rahul@demo.edu</p>
            </div>
            <Button variant="ghost" size="sm" className="text-gray-400" onClick={() => setView('landing')}>
              <LogIn className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Welcome Banner */}
        <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/20">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">Welcome back, Rahul!</h1>
              <p className="text-gray-400">Continue your GST filing practice in our Virtual Tax Lab</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Employability Status</p>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-lg px-3 py-1">
                JOB READY
              </Badge>
            </div>
          </div>
        </div>

        {/* Progress Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'GSTR-1 Score', value: '92%', icon: FileText, color: 'text-blue-400', status: 'Completed' },
            { label: 'GSTR-3B Score', value: '88%', icon: FileSpreadsheet, color: 'text-purple-400', status: 'Completed' },
            { label: 'GST Registration', value: '100%', icon: UserPlus, color: 'text-emerald-400', status: 'Completed' },
            { label: 'Scorecard', value: 'Ready', icon: Award, color: 'text-amber-400', status: 'Download' }
          ].map((stat, i) => (
            <Card key={i} className="bg-[#111827] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs">{stat.status}</Badge>
                </div>
                <p className="text-xs text-gray-400 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modules */}
        <h2 className="text-lg font-semibold text-white mb-4">GST Virtual Lab Modules</h2>
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {[
            {
              title: 'GST Registration',
              subtitle: 'New Registration Application',
              icon: UserPlus,
              gradient: 'from-emerald-500 to-teal-500',
              status: 'Completed',
              accuracy: 100,
              attempts: 1
            },
            {
              title: 'GSTR-1',
              subtitle: 'Outward Supplies Return',
              icon: FileText,
              gradient: 'from-blue-500 to-cyan-500',
              status: 'Completed',
              accuracy: 92,
              attempts: 2
            },
            {
              title: 'GSTR-3B',
              subtitle: 'Summary Return',
              icon: FileSpreadsheet,
              gradient: 'from-purple-500 to-pink-500',
              status: 'Completed',
              accuracy: 88,
              attempts: 1
            }
          ].map((module, i) => (
            <Card key={i} className="bg-[#111827] border-white/10 hover:border-teal-500/50 transition-all cursor-pointer group">
              <div className={`h-1.5 bg-gradient-to-r ${module.gradient}`}></div>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <module.icon className="w-7 h-7 text-white" />
                  </div>
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> {module.status}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{module.title}</h3>
                <p className="text-sm text-gray-400 mb-4">{module.subtitle}</p>
                
                <div className="flex items-center justify-between text-sm mb-4">
                  <div>
                    <span className="text-gray-500">Accuracy: </span>
                    <span className={module.accuracy >= 90 ? 'text-emerald-400' : 'text-amber-400'}>{module.accuracy}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Attempts: </span>
                    <span className="text-white">{module.attempts}</span>
                  </div>
                </div>

                <Button className={`w-full bg-gradient-to-r ${module.gradient} hover:opacity-90 text-white`}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Practice Again
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Advanced Modules - Pro */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-white">Advanced Modules</h2>
            <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/30">
              <Crown className="w-3 h-3 mr-1" /> Pro
            </Badge>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'GST Reconciliation',
                subtitle: 'GSTR-2A/2B vs Books',
                description: 'ITC validation and mismatch identification',
                icon: Target,
                status: 'Available'
              },
              {
                title: 'GSTR-9',
                subtitle: 'Annual Return',
                description: 'Comprehensive yearly consolidation of all GST returns',
                icon: FileText,
                status: 'Available'
              },
              {
                title: 'GSTR-9C',
                subtitle: 'Reconciliation Statement',
                description: 'Audit-ready compliance and reconciliation',
                icon: ClipboardCheck,
                status: 'Available'
              }
            ].map((module, i) => (
              <Card key={i} className="bg-amber-950/20 border-amber-800/30 hover:border-amber-700/50 transition-all cursor-pointer group">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-xl bg-amber-800/50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <module.icon className="w-7 h-7 text-amber-400" />
                    </div>
                    <Badge className="bg-amber-900/40 text-amber-400 border-0">
                      {module.status}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{module.title}</h3>
                  <p className="text-sm text-amber-500/70 mb-2">{module.subtitle}</p>
                  <p className="text-sm text-gray-400 mb-4">{module.description}</p>
                  <Button className="w-full bg-amber-700 hover:bg-amber-600 text-white">
                    Start Module
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Scorecard Section */}
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Employability Scorecard Ready!</h3>
                    <p className="text-sm text-gray-400">You've achieved 85%+ accuracy. Download your verified scorecard.</p>
                  </div>
                </div>
                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400">
                  <Download className="w-4 h-4 mr-2" /> Download Scorecard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  // Render based on view state
  switch (view) {
    case 'admin-login':
      return <AdminLoginView />;
    case 'admin-dashboard':
      return <AdminDashboardView />;
    case 'student-login':
      return <StudentLoginView />;
    case 'student-dashboard':
      return <StudentDashboardView />;
    default:
      return <LandingView />;
  }
};

export default DemoInstitutePage;
